import { useCallback, useEffect, useMemo, useState } from "react";
import { getApiBase } from "../../apiBase";
import "./AdminOperationsPage.css";

const API_BASE = getApiBase();

function formatDateTime(value) {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatProviderList(providers) {
  if (!Array.isArray(providers) || !providers.length) {
    return "No provider data";
  }
  return providers.join(", ");
}

function formatQuotaUsage(user) {
  const quota = Number(user?.mediaQuotaGb);
  const used = Number(user?.usedMediaGb);
  if (!Number.isFinite(quota) || !Number.isFinite(used)) {
    return "No quota data";
  }
  return `${used.toFixed(2)} GB of ${quota.toFixed(2)} GB`;
}

function formatHealthLabel(status) {
  if (!status) {
    return "Unknown";
  }
  return status.replace(/_/g, " ");
}

function formatReleaseReason(reason) {
  if (!reason) {
    return "Unknown";
  }
  return reason.replace(/_/g, " ");
}

function pickPagerItems(data) {
  if (Array.isArray(data.users)) {
    return data.users;
  }
  if (Array.isArray(data.leads)) {
    return data.leads;
  }
  if (Array.isArray(data.customers)) {
    return data.customers;
  }
  if (Array.isArray(data.cases)) {
    return data.cases;
  }
  return [];
}

async function sendAdminJson(path, authToken, method, payload, fallbackMessage) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || fallbackMessage);
  }
  return data;
}

function useAdminPager(authToken, endpoint, limit) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [nextCursor, setNextCursor] = useState(null);
  const [currentCursor, setCurrentCursor] = useState(null);
  const [cursorStack, setCursorStack] = useState([]);

  const loadPage = useCallback(
    async (cursor = null, previousCursors = []) => {
      if (!authToken) {
        return;
      }
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams();
        params.set("limit", String(limit));
        if (cursor) {
          params.set("cursor", cursor);
        }
        const res = await fetch(`${API_BASE}${endpoint}?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.message || "Could not load admin data");
        }
        setItems(pickPagerItems(data));
        setSummary(data.summary || {});
        setNextCursor(data.pagination?.nextCursor || null);
        setCurrentCursor(cursor);
        setCursorStack(previousCursors);
      } catch (loadError) {
        setError(loadError.message || "Could not load admin data");
      } finally {
        setLoading(false);
      }
    },
    [authToken, endpoint, limit]
  );

  useEffect(() => {
    setItems([]);
    setSummary({});
    setNextCursor(null);
    setCurrentCursor(null);
    setCursorStack([]);
    if (authToken) {
      loadPage(null, []);
    }
  }, [authToken, loadPage]);

  const pageNumber = cursorStack.length + 1;

  return {
    items,
    summary,
    loading,
    error,
    pageNumber,
    canGoBack: cursorStack.length > 0,
    canGoForward: Boolean(nextCursor),
    refresh: () => loadPage(currentCursor, cursorStack),
    goNext: () => {
      if (!nextCursor) {
        return;
      }
      loadPage(nextCursor, [...cursorStack, currentCursor]);
    },
    goBack: () => {
      if (!cursorStack.length) {
        return;
      }
      const previousCursor = cursorStack[cursorStack.length - 1] || null;
      loadPage(previousCursor, cursorStack.slice(0, -1));
    },
  };
}

function LeadStatusEditor({ lead, onSave }) {
  const [status, setStatus] = useState(lead.status || "new");
  const [ownerNote, setOwnerNote] = useState(lead.ownerNote || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setStatus(lead.status || "new");
    setOwnerNote(lead.ownerNote || "");
    setError("");
    setSaving(false);
  }, [lead.inquiryId, lead.ownerNote, lead.status]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await onSave(lead.inquiryId, { status, ownerNote });
    } catch (saveError) {
      setError(saveError.message || "Could not save lead");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-ops-lead-editor">
      <div className="admin-ops-inline-fields">
        <label className="admin-ops-field">
          <span>Status</span>
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="new">New</option>
            <option value="reviewing">Reviewing</option>
            <option value="followed_up">Followed up</option>
            <option value="closed">Closed</option>
          </select>
        </label>
      </div>
      <label className="admin-ops-field">
        <span>Owner note</span>
        <textarea
          rows={3}
          value={ownerNote}
          onChange={(event) => setOwnerNote(event.target.value)}
          placeholder="Internal follow-up note for the operations team"
        />
      </label>
      {error ? <p className="error-text">{error}</p> : null}
      <button type="button" className="admin-ops-save-btn" onClick={save} disabled={saving}>
        {saving ? "Saving…" : "Save lead status"}
      </button>
    </div>
  );
}

function ReleaseReviewCaseEditor({ reviewCase, onAssign, onAddNote, onTakeAction }) {
  const [note, setNote] = useState(reviewCase.reviewerNotes || "");
  const [action, setAction] = useState("start_review");
  const [saving, setSaving] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setNote(reviewCase.reviewerNotes || "");
    setAction("start_review");
    setSaving("");
    setError("");
  }, [reviewCase.caseId, reviewCase.reviewerNotes]);

  const saveNote = async () => {
    setSaving("note");
    setError("");
    try {
      await onAddNote(reviewCase.caseId, note);
    } catch (saveError) {
      setError(saveError.message || "Could not save note");
    } finally {
      setSaving("");
    }
  };

  const assignToMe = async () => {
    setSaving("assign");
    setError("");
    try {
      await onAssign(reviewCase.caseId);
    } catch (saveError) {
      setError(saveError.message || "Could not assign case");
    } finally {
      setSaving("");
    }
  };

  const runAction = async () => {
    setSaving("action");
    setError("");
    try {
      await onTakeAction(reviewCase.caseId, action);
    } catch (saveError) {
      setError(saveError.message || "Could not update case");
    } finally {
      setSaving("");
    }
  };

  return (
    <div className="admin-ops-lead-editor admin-ops-review-editor">
      <div className="admin-ops-inline-fields">
        <button
          type="button"
          className="admin-ops-page-btn"
          onClick={assignToMe}
          disabled={saving === "assign"}
        >
          {saving === "assign" ? "Assigning…" : "Assign to me"}
        </button>
        <label className="admin-ops-field">
          <span>Case action</span>
          <select value={action} onChange={(event) => setAction(event.target.value)}>
            <option value="start_review">Start review</option>
            <option value="waiting_for_owner">Waiting for owner</option>
            <option value="resolve_case">Resolve case</option>
            <option value="close_no_action">Close with no action</option>
          </select>
        </label>
        <button
          type="button"
          className="admin-ops-save-btn"
          onClick={runAction}
          disabled={saving === "action"}
        >
          {saving === "action" ? "Saving…" : "Apply action"}
        </button>
      </div>
      <label className="admin-ops-field">
        <span>Reviewer note</span>
        <textarea
          rows={4}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Add context for the next reviewer or the follow-up decision."
        />
      </label>
      {error ? <p className="error-text">{error}</p> : null}
      <button type="button" className="admin-ops-save-btn" onClick={saveNote} disabled={saving === "note"}>
        {saving === "note" ? "Saving…" : "Save review note"}
      </button>
    </div>
  );
}

export function AdminOperationsPage({ authToken }) {
  const [activeTab, setActiveTab] = useState("users");
  const usersPager = useAdminPager(authToken, "/api/admin/users", 12);
  const leadsPager = useAdminPager(authToken, "/api/admin/guest-leads", 10);
  const inactivePager = useAdminPager(authToken, "/api/admin/alive-tracker/inactive", 10);
  const releaseReviewPager = useAdminPager(authToken, "/api/admin/release-review/cases", 10);

  const saveLead = useCallback(
    async (inquiryId, payload) => {
      await sendAdminJson(
        `/api/admin/guest-leads/${encodeURIComponent(inquiryId)}`,
        authToken,
        "PATCH",
        payload,
        "Could not update lead"
      );
      await leadsPager.refresh();
    },
    [authToken, leadsPager]
  );

  const saveReviewAssignment = useCallback(
    async (caseId) => {
      await sendAdminJson(
        `/api/admin/release-review/cases/${encodeURIComponent(caseId)}/assign`,
        authToken,
        "POST",
        { assignToSelf: true },
        "Could not assign case"
      );
      await releaseReviewPager.refresh();
    },
    [authToken, releaseReviewPager]
  );

  const saveReviewNote = useCallback(
    async (caseId, reviewerNotes) => {
      await sendAdminJson(
        `/api/admin/release-review/cases/${encodeURIComponent(caseId)}/note`,
        authToken,
        "POST",
        { note: reviewerNotes },
        "Could not save note"
      );
      await releaseReviewPager.refresh();
    },
    [authToken, releaseReviewPager]
  );

  const saveReviewAction = useCallback(
    async (caseId, action) => {
      await sendAdminJson(
        `/api/admin/release-review/cases/${encodeURIComponent(caseId)}/action`,
        authToken,
        "POST",
        { action },
        "Could not save action"
      );
      await releaseReviewPager.refresh();
    },
    [authToken, releaseReviewPager]
  );

  const activePager =
    activeTab === "users"
      ? usersPager
      : activeTab === "leads"
        ? leadsPager
        : activeTab === "inactive"
          ? inactivePager
          : releaseReviewPager;
  const userCount = Number(usersPager.summary?.totalUsers || 0);
  const leadCount = Number(leadsPager.summary?.totalLeads || 0);
  const inactiveCount = Number(inactivePager.summary?.totalFlaggedCustomers || 0);
  const releaseReviewCount = Number(releaseReviewPager.summary?.totalOpen || 0);

  const leadItems = useMemo(() => leadsPager.items || [], [leadsPager.items]);
  const inactiveItems = useMemo(() => inactivePager.items || [], [inactivePager.items]);
  const releaseReviewItems = useMemo(() => releaseReviewPager.items || [], [releaseReviewPager.items]);

  return (
    <section className="panel admin-ops-panel">
      <div className="admin-ops-head">
        <div>
          <p className="eyebrow">Owner operations</p>
          <h2>Back-office console for users, leads, and follow-up work.</h2>
          <p className="helper-text">
            This section stays outside the customer-facing journey, but uses the same sign-in and
            Lambda stack so operational workflows remain close to the product data.
          </p>
        </div>
        <div className="admin-ops-kpis" aria-label="Operations overview">
          <div className="admin-ops-kpi">
            <span>Total users</span>
            <strong>{userCount}</strong>
          </div>
          <div className="admin-ops-kpi">
            <span>Lead requests</span>
            <strong>{leadCount}</strong>
          </div>
          <div className="admin-ops-kpi">
            <span>Needs attention</span>
            <strong>{inactiveCount}</strong>
          </div>
          <div className="admin-ops-kpi">
            <span>Release review</span>
            <strong>{releaseReviewCount}</strong>
          </div>
        </div>
      </div>

      <div className="admin-ops-tabs" role="tablist" aria-label="Operations sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "users"}
          className={`admin-ops-tab ${activeTab === "users" ? "is-active" : ""}`}
          onClick={() => setActiveTab("users")}
        >
          Users
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "leads"}
          className={`admin-ops-tab ${activeTab === "leads" ? "is-active" : ""}`}
          onClick={() => setActiveTab("leads")}
        >
          Lead requests
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "inactive"}
          className={`admin-ops-tab ${activeTab === "inactive" ? "is-active" : ""}`}
          onClick={() => setActiveTab("inactive")}
        >
          Inactive customers
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "release-review"}
          className={`admin-ops-tab ${activeTab === "release-review" ? "is-active" : ""}`}
          onClick={() => setActiveTab("release-review")}
        >
          Release review
        </button>
      </div>

      {activePager.error ? (
        <p className="dashboard-notice admin-ops-notice" role="status">
          {activePager.error}
        </p>
      ) : null}

      {activeTab === "users" ? (
        <div className="admin-ops-section">
          <div className="admin-ops-list-head">
            <div>
              <h3>Registered users</h3>
              <p className="helper-text">
                Review account owners, sign-in providers, verification status, and storage posture.
              </p>
            </div>
          </div>

          {usersPager.loading ? (
            <p className="helper-text">Loading users…</p>
          ) : (
            <div className="admin-ops-card-list">
              {usersPager.items.map((user) => (
                <article key={user.userId} className="admin-ops-card">
                  <div className="admin-ops-card-top">
                    <div>
                      <h4>{user.name || "Unknown user"}</h4>
                      <p className="admin-ops-subline">{user.email || "No email on profile"}</p>
                    </div>
                    <span className="admin-ops-status-pill">{user.verificationStatus || "unknown"}</span>
                  </div>
                  <dl className="admin-ops-metadata">
                    <div>
                      <dt>User ID</dt>
                      <dd>{user.userId}</dd>
                    </div>
                    <div>
                      <dt>Providers</dt>
                      <dd>{formatProviderList(user.providers)}</dd>
                    </div>
                    <div>
                      <dt>Joined</dt>
                      <dd>{formatDateTime(user.createdAt)}</dd>
                    </div>
                    <div>
                      <dt>Updated</dt>
                      <dd>{formatDateTime(user.updatedAt)}</dd>
                    </div>
                    <div>
                      <dt>Media usage</dt>
                      <dd>{formatQuotaUsage(user)}</dd>
                    </div>
                  </dl>
                </article>
              ))}
            </div>
          )}
        </div>
      ) : activeTab === "leads" ? (
        <div className="admin-ops-section">
          <div className="admin-ops-list-head">
            <div>
              <h3>Lead requests</h3>
              <p className="helper-text">
                Review contact requests, keep internal notes, and mark follow-up progress without
                leaving the product dataset.
              </p>
            </div>
          </div>

          {leadsPager.loading ? (
            <p className="helper-text">Loading lead requests…</p>
          ) : (
            <div className="admin-ops-card-list">
              {leadItems.map((lead) => (
                <article key={lead.inquiryId} className="admin-ops-card admin-ops-lead-card">
                  <div className="admin-ops-card-top">
                    <div>
                      <h4>{lead.name || "Unnamed lead"}</h4>
                      <p className="admin-ops-subline">{lead.email || "No email provided"}</p>
                    </div>
                    <span className="admin-ops-status-pill">{lead.status || "new"}</span>
                  </div>
                  <p className="admin-ops-lead-message">{lead.message || "No message"}</p>
                  <dl className="admin-ops-metadata">
                    <div>
                      <dt>Submitted</dt>
                      <dd>{formatDateTime(lead.createdAt)}</dd>
                    </div>
                    <div>
                      <dt>Source</dt>
                      <dd>{lead.source || "guest_widget"}</dd>
                    </div>
                    <div>
                      <dt>Last updated</dt>
                      <dd>{formatDateTime(lead.updatedAt || lead.createdAt)}</dd>
                    </div>
                  </dl>
                  <LeadStatusEditor lead={lead} onSave={saveLead} />
                </article>
              ))}
            </div>
          )}
        </div>
      ) : activeTab === "inactive" ? (
        <div className="admin-ops-section">
          <div className="admin-ops-list-head">
            <div>
              <h3>Inactive or unacknowledged customers</h3>
              <p className="helper-text">
                Track customers who have missed calm weekly check-ins so the operations team can
                review who may need a quieter follow-up path.
              </p>
            </div>
          </div>

          {inactivePager.loading ? (
            <p className="helper-text">Loading inactive-customer review queue…</p>
          ) : (
            <div className="admin-ops-card-list">
              {inactiveItems.map((customer) => (
                <article key={customer.userId} className="admin-ops-card admin-ops-inactive-card">
                  <div className="admin-ops-card-top">
                    <div>
                      <h4>{customer.name || "Unknown user"}</h4>
                      <p className="admin-ops-subline">{customer.email || "No email on profile"}</p>
                    </div>
                    <span className={`admin-ops-status-pill admin-ops-status-pill--${customer.healthStatus || "unknown"}`}>
                      {formatHealthLabel(customer.healthStatus)}
                    </span>
                  </div>
                  <dl className="admin-ops-metadata">
                    <div>
                      <dt>Missed weekly checks</dt>
                      <dd>{Number(customer.missedCount || 0)}</dd>
                    </div>
                    <div>
                      <dt>Awaiting acknowledgement</dt>
                      <dd>{customer.awaitingAcknowledgement ? "Yes" : "No"}</dd>
                    </div>
                    <div>
                      <dt>Last sent</dt>
                      <dd>{formatDateTime(customer.lastSentAt)}</dd>
                    </div>
                    <div>
                      <dt>Last acknowledged</dt>
                      <dd>{formatDateTime(customer.lastAcknowledgedAt)}</dd>
                    </div>
                    <div>
                      <dt>Next scheduled check</dt>
                      <dd>{formatDateTime(customer.nextCheckAt)}</dd>
                    </div>
                    <div>
                      <dt>Weekly notes sent</dt>
                      <dd>{Number(customer.sentCount || 0)}</dd>
                    </div>
                  </dl>
                  <div className="admin-ops-message-preview">
                    <span>Last weekly message</span>
                    <strong>{customer.lastMessageSubject || "Weekly all-good check-in"}</strong>
                    <p>{customer.lastMessagePreview || "No preview recorded yet."}</p>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="admin-ops-section">
          <div className="admin-ops-list-head">
            <div>
              <h3>Quiet release review queue</h3>
              <p className="helper-text">
                These cases reached manual review after the owner check-in and reminder path ran
                out. Review them calmly before any protected next step is considered.
              </p>
            </div>
          </div>

          {releaseReviewPager.loading ? (
            <p className="helper-text">Loading release-review cases…</p>
          ) : (
            <div className="admin-ops-card-list">
              {releaseReviewItems.map((reviewCase) => (
                <article key={reviewCase.caseId} className="admin-ops-card admin-ops-review-card">
                  <div className="admin-ops-card-top">
                    <div>
                      <h4>{reviewCase.ownerName || "Unknown owner"}</h4>
                      <p className="admin-ops-subline">{reviewCase.ownerEmail || "No owner email on file"}</p>
                    </div>
                    <div className="admin-ops-review-pill-stack">
                      <span className={`admin-ops-status-pill admin-ops-status-pill--${reviewCase.reviewStatus || "open"}`}>
                        {formatHealthLabel(reviewCase.reviewStatus)}
                      </span>
                      <span className={`admin-ops-status-pill admin-ops-status-pill--${reviewCase.priority || "normal"}`}>
                        {reviewCase.priority || "normal"} priority
                      </span>
                    </div>
                  </div>
                  <dl className="admin-ops-metadata">
                    <div>
                      <dt>Workflow stage</dt>
                      <dd>{formatHealthLabel(reviewCase.workflowStatus)}</dd>
                    </div>
                    <div>
                      <dt>Reason</dt>
                      <dd>{formatReleaseReason(reviewCase.reason)}</dd>
                    </div>
                    <div>
                      <dt>Last activity</dt>
                      <dd>{formatDateTime(reviewCase.lastActivityAt)}</dd>
                    </div>
                    <div>
                      <dt>Last owner confirmation</dt>
                      <dd>{formatDateTime(reviewCase.lastConfirmedAt)}</dd>
                    </div>
                    <div>
                      <dt>Last owner check-in</dt>
                      <dd>{formatDateTime(reviewCase.lastCheckSentAt)}</dd>
                    </div>
                    <div>
                      <dt>Last reminder</dt>
                      <dd>{formatDateTime(reviewCase.lastReminderSentAt)}</dd>
                    </div>
                    <div>
                      <dt>Trusted contact ping</dt>
                      <dd>{formatDateTime(reviewCase.lastTrustedContactPingAt)}</dd>
                    </div>
                    <div>
                      <dt>Assigned to</dt>
                      <dd>{reviewCase.assignedTo || "Unassigned"}</dd>
                    </div>
                  </dl>
                  <div className="admin-ops-message-preview">
                    <span>Recommended next step</span>
                    <strong>{reviewCase.recommendedAction || "Review recent activity before taking action."}</strong>
                    <p>{reviewCase.resolutionSummary || reviewCase.reviewerNotes || "No reviewer summary yet."}</p>
                  </div>
                  <ReleaseReviewCaseEditor
                    reviewCase={reviewCase}
                    onAssign={saveReviewAssignment}
                    onAddNote={saveReviewNote}
                    onTakeAction={saveReviewAction}
                  />
                </article>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="admin-ops-pagination" aria-label="Operations pagination">
        <button
          type="button"
          className="admin-ops-page-btn"
          onClick={activePager.goBack}
          disabled={!activePager.canGoBack || activePager.loading}
        >
          Previous
        </button>
        <span className="admin-ops-page-status">Page {activePager.pageNumber}</span>
        <button
          type="button"
          className="admin-ops-page-btn"
          onClick={activePager.goNext}
          disabled={!activePager.canGoForward || activePager.loading}
        >
          Next
        </button>
      </div>
    </section>
  );
}
